<template>
  <b-modal
    v-model="show"
    size="xl"
    scrollable
    hide-footer
    no-close-on-backdrop
    body-class="form-preview-modal-body"
  >
    <template #modal-title>
      <div>
        <span>Form preview</span>
        <small v-if="mode === 'onboarding'" class="text-muted ml-50">onboarding #{{ onboardingId }}</small>
      </div>
      <div v-if="packagesUsed.length" class="mt-25">
        <b-badge
          v-for="pkg in packagesUsed"
          :key="pkg.id || pkg.Name"
          :variant="pkg.isDefault ? 'light-success' : 'light-primary'"
          class="mr-25 font-weight-normal"
        >
          {{ pkg.Name }}
          <span class="text-muted">· {{ pkg.isDefault ? 'Default' : 'Manual' }}</span>
        </b-badge>
      </div>
    </template>

    <b-card v-if="mode === 'selector'" border-variant="light" class="shadow-none mb-1 preview-selector">
      <b-row>
        <b-col cols="12" md="3">
          <b-form-group label="Client type" class="mb-50">
            <b-form-select v-model="selector.clientType" :options="clientTypeOptions" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Account type" class="mb-50">
            <b-form-select v-model="selector.accountType" :options="accountTypeOptions" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Client category" class="mb-50">
            <b-form-select v-model="selector.clientCategory" :options="clientCategoryOptions" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3" class="d-flex align-items-end">
          <b-button variant="primary" block class="mb-50" :disabled="loading || !selectorComplete" @click="loadSelector">
            <feather-icon icon="EyeIcon" class="mr-25" /> Load preview
          </b-button>
        </b-col>
      </b-row>
      <b-form-group label="Extra packages (added by hand)" class="mb-0">
        <v-select
          v-model="selector.packageIds"
          :options="manualPackages"
          label="Name"
          :reduce="p => p.id"
          multiple
          placeholder="None – default packages only"
        />
        <small class="text-muted">
          Default packages matching the selectors above are always included.
        </small>
      </b-form-group>
    </b-card>

    <b-overlay :show="loading" rounded="sm">
      <div v-if="payload" class="form-preview-wrap">
        <form-preview :payload="payload" />
      </div>
      <div v-else class="text-center text-muted py-3">
        <span v-if="loading">Assembling the form…</span>
        <span v-else-if="mode === 'selector'">Pick the client type, account type and category, then load the preview.</span>
        <span v-else>No preview loaded.</span>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal, BOverlay, BCard, BRow, BCol, BFormGroup, BFormSelect, BButton, BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axiosIns from '@/libs/axios'
import FormPreview from '@/views/onboarding/public/FormPreview.vue'
import { apiError } from './builderHelpers'

const FALLBACK = {
  clientTypes: ['Company', 'Individual'],
  accountTypes: ['DVP', 'MARGIN', 'CUSTODIAN'],
  clientCategories: ['Professional', 'Retail'],
}

export default {
  name: 'FormPreviewModal',

  components: {
    BModal, BOverlay, BCard, BRow, BCol, BFormGroup, BFormSelect, BButton, BBadge, vSelect, FormPreview,
  },

  props: {
    // Builder catalogue (packages, clientTypes, accountTypes, clientCategories). Optional so the
    // modal can be used in onboarding mode from pages that do not load the builder.
    data: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      show: false,
      loading: false,
      mode: 'selector',
      onboardingId: null,
      selector: {
        clientType: null,
        accountType: null,
        clientCategory: null,
        packageIds: [],
      },
      payload: null,
    }
  },

  computed: {
    clientTypeOptions() {
      return this.toOptions(this.data.clientTypes || FALLBACK.clientTypes)
    },
    accountTypeOptions() {
      return this.toOptions(this.data.accountTypes || FALLBACK.accountTypes)
    },
    clientCategoryOptions() {
      return this.toOptions(this.data.clientCategories || FALLBACK.clientCategories)
    },
    manualPackages() {
      return (this.data.packages || []).filter(p => !p.IsDefault)
    },
    selectorComplete() {
      const s = this.selector
      return !!(s.clientType && s.accountType && s.clientCategory)
    },
    packagesUsed() {
      return (this.payload && this.payload.packages) || []
    },
  },

  methods: {
    toOptions(list) {
      return [{ value: null, text: 'Pick…' }].concat((list || []).map(v => ({ value: v, text: v })))
    },

    // open({ onboardingId }) or open({ clientType, accountType, clientCategory, packageIds }).
    open(options) {
      const opts = options || {}
      this.payload = null
      if (opts.onboardingId) {
        this.mode = 'onboarding'
        this.onboardingId = opts.onboardingId
        this.show = true
        this.request({ onboardingId: opts.onboardingId })
        return
      }
      this.mode = 'selector'
      this.onboardingId = null
      this.selector = {
        clientType: opts.clientType || null,
        accountType: opts.accountType || null,
        clientCategory: opts.clientCategory || null,
        packageIds: [...(opts.packageIds || [])],
      }
      this.show = true
      if (this.selectorComplete) this.loadSelector()
    },

    loadSelector() {
      if (!this.selectorComplete) return
      const s = this.selector
      this.request({
        clientType: s.clientType,
        accountType: s.accountType,
        clientCategory: s.clientCategory,
        packageIds: s.packageIds || [],
      })
    },

    request(body) {
      this.loading = true
      // called straight through axios so the modal also works on pages that do not
      // register the settings store module (the onboarding detail page)
      axiosIns.post('previewOnboardingForm', body)
        .then(res => {
          this.payload = res.data || null
        })
        .catch(err => {
          this.payload = null
          this.$bvToast.toast(apiError(err, 'Could not build the preview.'), { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.loading = false })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.form-preview-modal-body {
  background: #f4f5f9;
}
.form-preview-wrap .client-onboarding-page {
  padding: 0;
  min-height: 0;
}
</style>
